/**
 * ChartDisplay Component
 * Displays Plotly charts in a secure iframe with expand/collapse functionality
 */

import React, { useState, useRef, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  IconButton,
  Collapse,
  Tooltip,
  Alert,
} from '@mui/material';
import LoadingIndicator from './LoadingIndicator';
import {
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  BarChart as ChartIcon,
  Fullscreen as FullscreenIcon,
  ContentCopy as CopyIcon,
} from '@mui/icons-material';

const ChartDisplay = ({ 
  htmlContent, 
  title = "Chart", 
  defaultHeight = 550,
  collapsible = true,
  defaultExpanded = true 
}) => {
  const [expanded, setExpanded] = useState(defaultExpanded);
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const iframeRef = useRef(null);

  // Reset loading state when content changes
  useEffect(() => {
    setIsLoading(true);
    setHasError(false);
  }, [htmlContent]);

  // Handle iframe load events
  const handleIframeLoad = () => {
    setIsLoading(false);
    
    // Try to communicate with iframe to ensure it loaded properly
    try {
      const iframe = iframeRef.current;
      if (iframe && iframe.contentWindow) {
        // Check if the iframe has content
        const iframeDoc = iframe.contentDocument || iframe.contentWindow.document;
        if (iframeDoc && iframeDoc.body && iframeDoc.body.innerHTML.trim() === '') {
          setHasError(true);
        }
      }
    } catch (e) {
      // Cross-origin restrictions might prevent access, but that's okay
      console.debug('Iframe access restricted (normal for security)');
    }
  };

  const handleIframeError = () => {
    setIsLoading(false);
    setHasError(true);
  };

  // Copy chart HTML to clipboard
  const handleCopyHtml = () => {
    if (htmlContent) {
      navigator.clipboard.writeText(htmlContent);
      // Could add a toast notification here
    }
  };

  // Toggle fullscreen view
  const handleFullscreen = () => {
    setIsFullscreen(!isFullscreen);
  };

  // Clean and prepare HTML content for iframe
  const prepareHtmlContent = (html) => {
    if (!html) return '';
    
    // Ensure HTML has proper structure
    if (!html.includes('<html>')) {
      return `
        <!DOCTYPE html>
        <html>
        <head>
          <meta charset="utf-8">
          <title>Chart</title>
          <style>
            body {
              margin: 0;
              padding: 16px 10px 20px;
              font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', sans-serif;
              background: white;
            }
            .plotly-graph-div {
              height: 100% !important;
              width: 100% !important;
            }
          </style>
        </head>
        <body>
          ${html}
        </body>
        </html>
      `;
    }
    
    return html;
  };

  if (!htmlContent) {
    return null;
  }

  const chartHeight = isFullscreen ? '80vh' : defaultHeight;

  return (
    <Paper 
      elevation={2} 
      sx={{ 
        mt: 2, 
        overflow: 'hidden',
        border: '1px solid',
        borderColor: 'divider',
      }}
    >
      {/* Header */}
      {collapsible && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            p: 1.5,
            backgroundColor: 'grey.50',
            borderBottom: expanded ? '1px solid' : 'none',
            borderColor: 'divider',
            cursor: 'pointer',
            '&:hover': {
              backgroundColor: 'grey.100',
            },
          }}
          onClick={() => setExpanded(!expanded)}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <ChartIcon color="primary" />
            <Typography variant="subtitle1" fontWeight="medium">
              {title}
            </Typography>
          </Box>
          
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {expanded && (
              <>
                <Tooltip title="Copy HTML">
                  <IconButton 
                    size="small" 
                    onClick={(e) => {
                      e.stopPropagation();
                      handleCopyHtml();
                    }}
                  >
                    <CopyIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Fullscreen">
                  <IconButton 
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleFullscreen();
                    }}
                  >
                    <FullscreenIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </>
            )}
            <IconButton size="small">
              {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </IconButton>
          </Box>
        </Box>
      )}

      {/* Chart Content */}
      <Collapse in={expanded || !collapsible}>
        <Box sx={{ position: 'relative', minHeight: chartHeight, maxHeight: '80vh', overflow: 'auto' }}>
          {/* Loading indicator */}
          {isLoading && (
            <Box
              sx={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                zIndex: 2,
              }}
            >
              <LoadingIndicator variant="spinner" size={36} color="#2C365E" />
              <Typography variant="body2" sx={{ mt: 1, textAlign: 'center' }}>
                Loading chart...
              </Typography>
            </Box>
          )}

          {/* Error state */}
          {hasError && (
            <Box sx={{ p: 2 }}>
              <Alert severity="error">
                Failed to load chart. The visualization data may be corrupted.
              </Alert>
            </Box>
          )}

          {/* Chart iframe */}
          <iframe
            ref={iframeRef}
            srcDoc={prepareHtmlContent(htmlContent)}
            style={{
              width: '100%',
              height: isFullscreen ? '100%' : `${typeof chartHeight === 'number' ? chartHeight : defaultHeight}px`,
              border: 'none',
              display: hasError ? 'none' : 'block',
            }}
            onLoad={handleIframeLoad}
            onError={handleIframeError}
            title={title}
            sandbox="allow-scripts allow-same-origin"
            loading="lazy"
          />
        </Box>
      </Collapse>

      {/* Fullscreen overlay */}
      {isFullscreen && (
        <Box
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.9)',
            zIndex: 9999,
            display: 'flex',
            flexDirection: 'column',
          }}
          onClick={handleFullscreen}
        >
          <Box
            sx={{
              p: 2,
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              color: 'white',
            }}
          >
            <Typography variant="h6">{title}</Typography>
            <IconButton 
              color="inherit" 
              onClick={handleFullscreen}
            >
              <ExpandLessIcon />
            </IconButton>
          </Box>
          
          <Box sx={{ flex: 1, p: 2 }} onClick={(e) => e.stopPropagation()}>
            <iframe
              srcDoc={prepareHtmlContent(htmlContent)}
              style={{
                width: '100%',
                height: '100%',
                border: 'none',
                borderRadius: '8px',
              }}
              title={`${title} - Fullscreen`}
              sandbox="allow-scripts allow-same-origin"
            />
          </Box>
        </Box>
      )}
    </Paper>
  );
};

export default ChartDisplay;